import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Users, Share2, Check, Lock } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function ShareToCommunity({ sermon, user, onSuccess, isOpen: externalIsOpen, onClose: externalOnClose }) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [alreadyShared, setAlreadyShared] = useState(false);

  // Support both controlled and uncontrolled modes
  const isControlled = externalIsOpen !== undefined;
  const isOpen = isControlled ? externalIsOpen : internalIsOpen;
  const setIsOpen = isControlled ? (open) => { if (!open && externalOnClose) externalOnClose(); } : setInternalIsOpen;

  // Check if user has Pro+ access
  const userTier = user?.subscription_tier?.toLowerCase() || 'trial';
  const hasAccess = ['pro', 'studio', 'church_teams'].includes(userTier);

  const handleOpen = () => {
    if (!hasAccess) {
      toast.error('Community features require Pro subscription or higher');
      return;
    }
    setIsOpen(true);
  };

  const handleShare = async () => {
    if (!sermon) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/community/sermons`, {
        title: sermon.title,
        description: description || `A sermon on ${sermon.topic || sermon.scripture_reference || 'various topics'}`,
        sermon_type: 'in_app',
        sermon_id: sermon.id,
        scripture_reference: sermon.scripture_reference,
        topic: sermon.topic,
        content_preview: sermon.content?.substring(0, 300),
        tags: tags.split(',').map(t => t.trim()).filter(Boolean)
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      toast.success('Sermon shared to Community!');
      setAlreadyShared(true);
      setIsOpen(false);
      setDescription('');
      setTags('');
      
      if (onSuccess) onSuccess();
      if (externalOnClose) externalOnClose();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Community features require Pro subscription');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to share sermon');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Only show button in uncontrolled mode */}
      {!isControlled && (
        <Button
          onClick={handleOpen}
          variant="outline"
          className={`${hasAccess ? 'border-purple-300 text-purple-700 hover:bg-purple-50' : 'border-gray-300 text-gray-500'}`}
          disabled={alreadyShared}
          title={!hasAccess ? 'Pro+ required' : alreadyShared ? 'Already shared' : 'Share to Community'}
        >
          {alreadyShared ? (
            <>
              <Check className="w-4 h-4 sm:mr-2 text-green-600" />
              <span className="hidden sm:inline text-green-600">Shared</span>
            </>
          ) : hasAccess ? (
            <>
              <Users className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Community</span>
            </>
          ) : (
            <>
              <Lock className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Pro+</span>
            </>
          )}
        </Button>
      )}

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Share to Community
            </DialogTitle>
            <DialogDescription>
              Share this sermon with fellow preachers in the PulpitPilot community
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Sermon Preview */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900">{sermon?.title}</h3>
              {sermon?.scripture_reference && (
                <p className="text-sm text-blue-600">{sermon.scripture_reference}</p>
              )}
              {sermon?.topic && (
                <p className="text-sm text-gray-600">Topic: {sermon.topic}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium mb-1">Add a note or description</label>
              <Textarea
                placeholder="Share your thoughts about this sermon..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium mb-1">Tags (comma-separated)</label>
              <Input
                placeholder="faith, grace, leadership"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
              />
            </div>

            {/* Share Button */}
            <Button 
              onClick={handleShare} 
              disabled={submitting}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              {submitting ? (
                'Sharing...'
              ) : (
                <>
                  <Share2 className="w-4 h-4 mr-2" />
                  Share to Community
                </>
              )}
            </Button>

            <p className="text-xs text-gray-500 text-center">
              Community members will be able to view, like, and comment on your sermon.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
